import { useMutation, useQuery } from '@tanstack/react-query'
import { useState, type FormEvent } from 'react'
import { api, ApiError } from '../lib/api'

/**
 * Change your own password.
 *
 * Wami had no way to do this — no change-password form, no reset email — so the only recovery
 * from a forgotten password was a support request, and the only response to a password you
 * believe is compromised was to keep using it.
 *
 * What the form asks for depends on how this session was authenticated, which the server reports
 * on `/auth/session`. Someone who signed in with an emailed code has already proven they hold
 * the mailbox — the same proof a reset link would have leaned on — so asking them for a password
 * they have by definition forgotten would close the loop they came here to escape. Someone who
 * signed in with the password is asked for it again, because otherwise an unattended laptop is
 * enough to take an account away from its owner.
 */
export function AccountSecurity() {
  const session = useQuery({ queryKey: ['session'], queryFn: api.session })

  const [current, setCurrent] = useState('')
  const [next, setNext] = useState('')
  const [confirmation, setConfirmation] = useState('')

  const change = useMutation({
    mutationFn: () =>
      api.changePassword({
        newPassword: next,
        ...(needsCurrent ? { currentPassword: current } : {}),
      }),
    onSuccess: () => {
      setCurrent('')
      setNext('')
      setConfirmation('')
    },
  })

  if (session.isPending) return <div className="cad-card" style={{ height: '200px' }} />

  const method = session.data?.authMethod ?? 'password'
  const needsCurrent = method === 'password'

  if (method === 'sso') {
    return (
      <section className="cad-card">
        <div className="stack gap-2">
          <h2 className="cad-section-title">Password</h2>
          <p className="cad-muted" style={{ maxWidth: '60ch' }}>
            You signed in through your organisation&rsquo;s identity provider, so there is no Wami
            password on this account. Change it wherever your organisation manages sign-in.
          </p>
        </div>
      </section>
    )
  }

  const mismatch = confirmation.length > 0 && next !== confirmation
  const tooShort = next.length > 0 && next.length < 8
  const ready = next.length >= 8 && next === confirmation && (!needsCurrent || current.length > 0)

  const submit = (event: FormEvent) => {
    event.preventDefault()
    if (ready) change.mutate()
  }

  return (
    <section className="cad-card">
      <form className="stack gap-3" onSubmit={submit} style={{ maxWidth: '46ch' }}>
        <div className="stack gap-1">
          <h2 className="cad-section-title">Password</h2>
          <p className="cad-muted">
            {needsCurrent
              ? 'Changing this signs you out everywhere else.'
              : 'You signed in with an emailed code, so you can set a new password without the old one. This signs you out everywhere else.'}
          </p>
        </div>

        {change.isError && (
          <p className="cad-note" role="alert">
            {change.error instanceof ApiError ? change.error.message : 'That did not work.'}
          </p>
        )}
        {change.isSuccess && (
          <p className="cad-note" role="status">
            Password changed. Other sessions have been signed out.
          </p>
        )}

        {needsCurrent && (
          <label className="cad-field">
            <span className="cad-eyebrow">Current password</span>
            <input
              className="cad-input"
              type="password"
              autoComplete="current-password"
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
            />
          </label>
        )}

        <label className="cad-field">
          <span className="cad-eyebrow">New password</span>
          <input
            className="cad-input"
            type="password"
            autoComplete="new-password"
            minLength={8}
            value={next}
            onChange={(e) => setNext(e.target.value)}
          />
          <span className="cad-muted">At least 8 characters.</span>
        </label>

        <label className="cad-field">
          <span className="cad-eyebrow">Confirm new password</span>
          <input
            className="cad-input"
            type="password"
            autoComplete="new-password"
            value={confirmation}
            onChange={(e) => setConfirmation(e.target.value)}
          />
          {/* Shown against the field rather than only on submit — retyping a long password to
              find out it never matched is the whole reason confirmation fields are disliked. */}
          {mismatch && <span className="cad-muted">These do not match.</span>}
          {tooShort && !mismatch && <span className="cad-muted">Too short.</span>}
        </label>

        <div className="row">
          <button type="submit" className="cad-btn" disabled={!ready || change.isPending}>
            {change.isPending ? 'Saving…' : 'Change password'}
          </button>
        </div>
      </form>
    </section>
  )
}
